import React, { Fragment, useState } from 'react';
import { UiInput } from '@open-stack/ui';
import { SpcCheckoutHeader } from '../header/SpcCheckoutHeader';
import { ProviderPayAdyen } from '../../../components/provider/pay/adyen/ProviderPayAdyen';
import { ProviderPayBambora } from '../../../components/provider/pay/bambora/ProviderPayBambora';
import { XAlert, XAlertType } from '../../../components/x/alert/XAlert';
import { ProviderPayVipps } from '../../../components/provider/pay/vipps/ProviderPayVipps';
import { ProviderPayBalance } from '../../../components/provider/pay/balance/ProviderPayBalance';
import moment from 'moment';
import times from 'lodash/times';
import { XSText } from '../../../xs/component/text/XSText';
import { XSBox } from '../../../xs/component/box/XSBox';
import { XSButton } from '../../../xs/component/button/XSButton';
import { XSIcon } from '../../../xs/component/icon/XSIcon';

type Response = {
    orderId?: number;
    paymentId?: number;
    type: 'SUCCESS' | 'REDIRECT' | 'ERROR';
    data?: any;
};

interface Props {
    response: {
        action: 'CHOOSE_PAYMENT_PROVIDER';
        providers: any[];
        totalCost: number;
        tipAmount?: number;
        orderProducts?: any[];
        orderId?: number;
        paymentId?: number;
        products?: any[];
        isTakeawayAtRequired?: boolean;
        availableTakeawayTimes?: string[];
    };
    isLoading: boolean;

    onNext(response: Response): any;

    onClose(): void;
}

export function SpcCheckoutChoosePaymentProvider({ response, onNext, isLoading, onClose }: Props) {
    const [takeawayAt, setTakeawayAt] = useState<string>();
    const {
        providers,
        totalCost,
        tipAmount,
        orderProducts,
        orderId,
        paymentId,
        products,
        availableTakeawayTimes = [],
    } = response;
    const configBase = { tipAmount, orderProducts, orderId, paymentId, products };
    const [isScheduleLater, setIsScheduleLater] = React.useState(false);
    const now = moment().startOf('minute').add(1, 'hour');

    const timeOptions = times(24 * 4)
        .map((i) =>
            moment()
                .startOf('day')
                .add(i * 15, 'minute'),
        )
        .filter((date) => date.isAfter(now))
        .map((date) => ({
            label: date.format('HH:mm'),
            value: date.format(),
        }));

    const hasTakeawayOptions = Boolean(response.isTakeawayAtRequired || availableTakeawayTimes.length > 0);
    const firstAvailableTime = availableTakeawayTimes.length > 0 ? availableTakeawayTimes[0] : timeOptions?.[0]?.value;

    React.useEffect(() => {
        if (hasTakeawayOptions && firstAvailableTime) {
            setTakeawayAt(firstAvailableTime);
        }
    }, [hasTakeawayOptions, String(firstAvailableTime)]);

    return (
        <>
            <SpcCheckoutHeader totalCost={totalCost} onClose={onClose} />

            {hasTakeawayOptions && (
                <>
                    <XSText weight={'bold'} align={'center'}>
                        When do you want to pick up your order?
                    </XSText>

                    <XSBox flex={'stretch'} gap={'md'}>
                        <XSButton
                            color={isScheduleLater ? 'default' : 'success'}
                            loading={isLoading}
                            onClick={() => {
                                //setTakeawayAt(undefined);
                                setTakeawayAt(firstAvailableTime);
                                setIsScheduleLater(false);
                            }}
                            direction={'column'}
                            style={{ gap: 0 }}>
                            <span>As soon as possible</span>

                            <XSBox gap={'sm'}>
                                <XSIcon size={14}>schedule</XSIcon>

                                <XSText size={'sm'}>
                                    {availableTakeawayTimes.length > 0
                                        ? moment(availableTakeawayTimes[0]).format('HH:mm')
                                        : timeOptions[0].label}
                                </XSText>
                            </XSBox>
                        </XSButton>

                        <XSButton
                            color={isScheduleLater ? 'success' : 'default'}
                            onClick={() => setIsScheduleLater(true)}>
                            Schedule for later
                        </XSButton>
                    </XSBox>

                    {isScheduleLater && (
                        <>
                            <UiInput
                                options={
                                    availableTakeawayTimes.length > 0
                                        ? availableTakeawayTimes.map((time) => ({
                                              label: moment(time).format('HH:mm'),
                                              value: time,
                                          }))
                                        : timeOptions
                                }
                                value={takeawayAt}
                                onChangeValue={setTakeawayAt}
                                placeholder={'Choose time'}
                            />
                        </>
                    )}
                </>
            )}

            {providers.map(({ provider, config }) => {
                let element;

                const configExtended = { ...configBase, ...config, takeawayAt };

                switch (provider as any & 'BALANCE') {
                    case 'ADYEN':
                        element = <ProviderPayAdyen config={configExtended} onSuccess={onNext} />;
                        break;
                    case 'BAMBORA':
                        element = <ProviderPayBambora config={configExtended} onSuccess={onNext} />;
                        break;
                    case 'VIPPS':
                        element = <ProviderPayVipps config={configExtended} onSuccess={onNext} />;
                        break;
                    // TODO: HARDCODED TYPE
                    case 'BALANCE':
                        element = <ProviderPayBalance config={configExtended} onSuccess={onNext} />;
                        break;
                    default:
                        element = <XAlert type={XAlertType.ERROR}>No payment provider found</XAlert>;
                        break;
                }

                return <Fragment key={provider}>{element}</Fragment>;
            })}

            {/*{isDevelopment && <ProviderPayDevelopment config={configBase} onSuccess={onNext} />}*/}
        </>
    );
}
